import React from 'react';
import { Box, Typography, Button, Table, TableBody, TableCell, TableHead, TableRow, TextField, MenuItem } from '@mui/material';

/**
 * "Available Learning Programs" — the P1A school view of My Programs.
 *
 * School Workspace deck slide 46 (SW-P1A-MyPrograms-Main) and Network Workspace
 * deck slide 64 (SNW-P1A-MyPrograms). The two slides are identical.
 *
 * This is NOT the P1B/LC program library. A P1A school does not author programs
 * — its partner does — so a library with Publish, Duplicate and an authoring
 * canvas would offer it nothing it can act on. It gets a list of what the
 * partner has suggested, built or declined, and one action per row.
 *
 * EVERY STRING HERE IS QUOTED FROM THE SLIDE. Status names in particular: the
 * partner sees the same exchange from the other side, and a reworded status
 * would stop the two matching.
 *
 * ONE DISCREPANCY IN THE SLIDE, left as its legend has it. The sample row for
 * "Linked" puts "View Evidence" in the Action cell, while the legend underneath
 * maps Linked → "View Dashboard". The legend is the explicit status-to-action
 * mapping, so it wins; flagged here rather than silently picked.
 */

const NAVY = '#052F44';
const ORANGE = 'var(--brand-primary, #FF6900)';
const MUTED = 'var(--brand-text-muted, #6B7280)';
const BORDER = 'var(--brand-border, #E5E7EB)';

/** The six statuses on slide 46. */
export const STATUS = {
    SUGGESTED_BY_PARTNER: 'Suggested by Partner',
    REQUESTED_BY_SCHOOL: 'Requested by School',
    PROGRAM_READY: 'Program Ready',
    LIVE: 'Live',
    LINKED: 'Linked',
    DECLINED: 'Declined',
};

/**
 * Slide 46's legend, verbatim: which group a status sits in, what it means, and
 * the button(s) it offers. `actions` is a list because Program Ready carries two
 * — View and Publish — where every other status carries one.
 */
export const STATUS_LEGEND = {
    [STATUS.SUGGESTED_BY_PARTNER]: {
        group: 'Needs attention',
        meaning: 'Program not yet delivered',
        actions: ['Review'],
    },
    [STATUS.REQUESTED_BY_SCHOOL]: {
        group: 'Needs attention',
        meaning: 'Program not yet delivered',
        actions: ['Review'],
    },
    [STATUS.PROGRAM_READY]: {
        // Its OWN group on slide 46 — the legend lists "Needs attention",
        // "Program Ready", "Active" and "–" as four separate headings, and a
        // delivered program is not something the school still has to chase.
        group: 'Program Ready',
        meaning: 'Partner created program and school is reviewing the program (it’s not published)',
        /*
         * View, then Request Changes (Ian / Usman, 2 Oct 2026). The school
         * previews the finished program before its partner publishes it, and
         * asks the partner for any changes; it cannot edit or publish it itself.
         * The partner's publish makes the row Live.
         */
        actions: ['View', 'Request Changes'],
    },
    [STATUS.LIVE]: {
        group: 'Active',
        meaning: '(program’s published) Partner created program and delivered to school, but not yet link to students',
        actions: ['Link Programs'],
    },
    [STATUS.LINKED]: {
        group: 'Active',
        meaning: 'linked to students',
        // The program can still be opened, as it could when it was published (7 Oct 2026).
        actions: ['View', 'View Dashboard'],
    },
    [STATUS.DECLINED]: {
        group: '–',
        meaning: 'Whoever suggested, declined by either party',
        actions: ['Contact Partner'],
    },
};

const headCell = {
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    color: MUTED,
    borderBottom: `1px solid ${BORDER}`,
    py: 1.25,
    whiteSpace: 'nowrap',
};

const bodyCell = {
    fontSize: '14px',
    color: 'var(--brand-text, #111827)',
    borderBottom: `1px solid ${BORDER}`,
    py: 1.75,
};

const Counter = ({ label, value }) => (
    <Box sx={{
        border: `1px solid ${BORDER}`,
        borderRadius: '10px',
        px: 2.5,
        py: 1.5,
        minWidth: 132,
        backgroundColor: 'var(--brand-surface, #ffffff)',
    }}>
        <Typography sx={{ fontSize: '22px', fontWeight: 800, color: NAVY, lineHeight: 1.1 }}>
            {value}
        </Typography>
        <Typography sx={{ fontSize: '12.5px', color: MUTED, mt: 0.25 }}>{label}</Typography>
    </Box>
);

/**
 * Status colour follows slide 46's own grouping, not one colour per status:
 * the three "Needs attention" statuses are drawn in the brand orange because
 * they are the rows a school has to act on, and the Active pair sit plain
 * because they need nothing. Declined is muted — closed, no action.
 */
const STATUS_TONE = {
    'Needs attention': { color: '#B14E00', bg: 'rgba(255, 105, 0, 0.10)' },
    // Program Ready became its own group, and without a tone of its own the
    // lookup below falls through to 'Active' — turning the orange chip grey.
    // It shares the Needs-attention orange: the school still has to act on it.
    'Program Ready':  { color: '#B14E00', bg: 'rgba(255, 105, 0, 0.10)' },
    'Active':         { color: NAVY,      bg: 'var(--brand-surface-subtle, #F3F4F6)' },
    '–':              { color: MUTED,     bg: 'var(--brand-surface-subtle, #F3F4F6)' },
};

const StatusChip = ({ status }) => {
    const group = STATUS_LEGEND[status]?.group;
    const tone = STATUS_TONE[group] || STATUS_TONE['Active'];
    return (
        <Typography component="span" sx={{
            display: 'inline-block',
            fontSize: '12px',
            fontWeight: 700,
            color: tone.color,
            backgroundColor: tone.bg,
            borderRadius: '6px',
            px: 1.25,
            py: '3px',
            whiteSpace: 'nowrap',
        }}>
            {status}
        </Typography>
    );
};

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

/* Same pager button as the rest of the portal — orange outline and text. */
const navBtnSx = {
    textTransform: 'none', fontSize: '12.5px', color: ORANGE, borderColor: ORANGE,
    '&:hover': { borderColor: '#e55e00', backgroundColor: 'rgba(255,105,0,0.08)' },
    '&.Mui-disabled': { color: ORANGE, borderColor: ORANGE, opacity: 0.4 },
};

/**
 * @param {Function} [actionHref] (action, row) → a link for that action, or ''.
 *   With one, the button is a native link ("Contact Partner" is a mailto the
 *   browser opens on the click itself); onAction still runs on the same click.
 * @param {string[]} [allStatuses] the slide status of every row on every page.
 *   The counters count these; without them, only the rows on this page.
 */
const AvailableLearningPrograms = ({
    rows = [], allStatuses = null, loading = false, onAction, onRequestPrograms, actionHref,
    page = 0, rowsPerPage = 10, total = 0, onPageChange, onRowsPerPageChange,
}) => {
    const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
    const data = Array.isArray(rows) ? rows : [];
    const statuses = Array.isArray(allStatuses) ? allStatuses : data.map((r) => r.status);
    const countGroup = (group) => statuses.filter((s) => STATUS_LEGEND[s]?.group === group).length;
    const countStatus = (status) => statuses.filter((s) => s === status).length;

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 3, maxWidth: 1240, mx: 'auto', width: '100%' }}>

            <Typography sx={{ fontSize: '23px', fontWeight: 800, color: NAVY, mt: 1 }}>
                Available Learning Programs
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Typography sx={{ fontSize: '14px', color: MUTED }}>
                    Programs created by your Partner, tailored to your assessment results.
                </Typography>
                {/*
                  * Entry point to the request wizard (SW/SNW slides 13-15).
                  * The slide places Take Action on Performance Summary, which is still a
                  * placeholder page — so the flow is reachable from here, where the
                  * resulting requests land, until that page is built.
                  */}
                {onRequestPrograms && (
                    <Button variant="contained" onClick={onRequestPrograms}
                            sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: ORANGE, color: '#fff', whiteSpace: 'nowrap', '&:hover': { backgroundColor: '#e55a00' } }}>
                        Request learning programs
                    </Button>
                )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                {/* Slide 46's header, in its order: Needs Attention, Program
                    Ready, Active, Linked, Declined. Declined was rendered twice. */}
                <Counter label="Needs Attention" value={countGroup('Needs attention')} />
                <Counter label="Program Ready" value={countStatus(STATUS.PROGRAM_READY)} />
                <Counter label="Active" value={countGroup('Active')} />
                <Counter label="Linked" value={countStatus(STATUS.LINKED)} />
                <Counter label="Declined" value={countStatus(STATUS.DECLINED)} />
            </Box>

            <Box sx={{
                border: `1px solid ${BORDER}`,
                borderRadius: '12px',
                overflowX: 'auto',
                backgroundColor: 'var(--brand-surface, #ffffff)',
            }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {['Received', 'Program', 'COS Area', 'Group', 'Learners', 'Duration', 'Status', 'Action'].map((h) => (
                                <TableCell key={h} sx={headCell}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.length === 0 ? (
                            <TableRow>
                                <TableCell sx={{ ...bodyCell, color: MUTED, textAlign: 'center', py: 5 }} colSpan={8}>
                                    {loading ? 'Loading…' : 'No programs from your Partner yet.'}
                                </TableCell>
                            </TableRow>
                        ) : data.map((row, i) => {
                            // A row may carry its own actions (e.g. Request Changes while
                            // the partner works on it); otherwise its status legend's.
                            const legend = { ...(STATUS_LEGEND[row.status] || { actions: [] }), ...(row.actions ? { actions: row.actions } : {}) };
                            return (
                                <TableRow key={`${row.program}-${i}`} hover>
                                    <TableCell sx={bodyCell}>
                                        {row.received}
                                        {row.isNew && (
                                            <Typography component="span" sx={{ fontSize: '12px', fontWeight: 700, color: ORANGE, ml: 0.75 }}>
                                                &middot; NEW
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ ...bodyCell, fontWeight: 700, color: NAVY }}>{row.program}</TableCell>
                                    <TableCell sx={bodyCell}>{row.cosArea}</TableCell>
                                    <TableCell sx={bodyCell}>{row.groupText || row.group}</TableCell>
                                    <TableCell sx={bodyCell}>{row.learners}</TableCell>
                                    <TableCell sx={bodyCell}>{row.duration}</TableCell>
                                    <TableCell sx={bodyCell}>
                                        <StatusChip status={row.status} />
                                        {/* One change request per program: it waits here while the
                                            partner makes the change, and is settled when it is published. */}
                                        {row.pendingChange && (
                                            <Typography sx={{ fontSize: '12px', color: MUTED, mt: 0.5 }}>
                                                Changes requested &middot; waiting for your partner
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={bodyCell}>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {legend.actions.map((action) => (
                                                <Button
                                                    key={action}
                                                    size="small"
                                                    variant="outlined"
                                                    {...((actionHref && actionHref(action, row))
                                                        ? { component: 'a', href: actionHref(action, row) }
                                                        : {})}
                                                    disabled={action === 'Request Changes' && !!row.pendingChange}
                                                    onClick={() => onAction && onAction(action, row)}
                                                    sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap' }}
                                                >
                                                    {action}
                                                </Button>
                                            ))}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>

            {/* Server-side pagination — same pager as the partner's request inbox. */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: 12.5, color: MUTED }}>Rows per page:</Typography>
                    <TextField select size="small" value={rowsPerPage}
                        onChange={(e) => onRowsPerPageChange && onRowsPerPageChange(Number(e.target.value))}
                        sx={{ width: 76, backgroundColor: 'var(--brand-surface, #fff)' }}>
                        {PAGE_SIZE_OPTIONS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                    </TextField>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: 12.5, color: MUTED }}>Page {page + 1} of {totalPages}</Typography>
                    <Button size="small" variant="outlined" sx={navBtnSx}
                            disabled={loading || page <= 0}
                            onClick={() => onPageChange && onPageChange(Math.max(0, page - 1))}>Previous</Button>
                    <Button size="small" variant="outlined" sx={navBtnSx}
                            disabled={loading || page + 1 >= totalPages}
                            onClick={() => onPageChange && onPageChange(page + 1)}>Next</Button>
                </Box>
            </Box>
        </Box>
    );
};

export default AvailableLearningPrograms;
